/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";

interface SearchResult {
  chunk: string;
  similarity: number;
  fullSchema: Record<string, any>;
}

interface SearchResultWithHash extends SearchResult {
  hash: string;
}

const ApiSourceEnum = z.enum([
  "chatbots",
  "tables",
  "interfaces",
  "zapguesser",
  "gulliver",
  "canvas",
  "implementation_registry",
  "authentications",
]);

export const SearchToolParams = z.object({
  query: z
    .string()
    .describe("The search query to find similar API specs chunks"),
  source: ApiSourceEnum.optional().describe(
    "Optional source filter to limit search to specific API spec sources",
  ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Searching for APIs...' or 'Finding documentation...'",
    ),
});

export type SearchToolParamsType = z.infer<typeof SearchToolParams>;

async function searchSimilarChunks(
  query: string,
  source?: string,
): Promise<SearchResult[]> {
  try {
    const response = await fetch("/api/canvasui/search", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        source: source,
      }),
    });

    if (!response.ok) {
      throw new Error(`Search request failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search request failed");
    }

    return data.results;
  } catch (error) {
    console.error("Error searching similar chunks:", error);
    throw error;
  }
}

export const searchToolDescription = `Searches for the most similar API specs and interface documentation that can help achieve the user's goal.
- query: The search query to find similar API specs and interface chunks - use before making a network request.
- source: Optional filter to search within specific embedding sources.`;

// Create a simple hash function for chunks
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Atom to store search chunks by hash
export const searchChunksAtom = atom<Record<string, SearchResult>>({});
searchChunksAtom.debugLabel = "searchChunksAtom";

export const searchToolAtom = atom<JotaiTool<z.infer<typeof SearchToolParams>>>(
  () => ({
    name: "search_tool",
    description: searchToolDescription,
    parameters: SearchToolParams,
    execute: atom(null, async (_get, set, { args: rawArgs }) => {
      let params: SearchToolParamsType;
      try {
        params = SearchToolParams.parse(rawArgs || {});
      } catch (error) {
        console.error(
          "[SEARCH_TOOL] Error parsing arguments for searchTool:",
          error,
        );
        return {
          content: [
            {
              type: "text",
              text: `Error: Invalid arguments provided for search_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
            },
          ],
        };
      }

      const { query, source } = params;

      try {
        const results = await searchSimilarChunks(query, source);
        const topResults = results.slice(0, 3);

        // Hash each chunk and store in atom
        const chunksWithHashes: SearchResultWithHash[] = topResults.map(
          (result) => {
            const hash = hashString(result.chunk);
            return { ...result, fullSchema: result.fullSchema, hash };
          },
        );

        // Update the atom with new chunks
        const chunkMap: Record<string, SearchResult> = {};
        chunksWithHashes.forEach(({ hash, chunk, similarity, fullSchema }) => {
          chunkMap[hash] = {
            chunk: chunk
              .replace(/\n\s*/g, " ") // Replace newlines + indentation with space
              .replace(/\s{2,}/g, " ") // Replace multiple spaces with single space
              .trim(), // Remove leading/trailing whitespace
            similarity,
            fullSchema,
          };
        });
        set(searchChunksAtom, chunkMap);

        // Return only the hashes and similarity scores
        const summaryResults = chunksWithHashes.map(({ hash, similarity }) => ({
          hash,
          similarity,
        }));

        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(summaryResults, null, 2),
            },
          ],
        };
      } catch (error) {
        console.error("[SEARCH_TOOL] Error in searchToolExecute:", error);
        return {
          content: [
            {
              type: "text",
              text: `Error searching similar chunks: ${error instanceof Error ? error.message : String(error)}`,
            },
          ],
        };
      }
    }),
  }),
);

searchToolAtom.debugLabel = "searchToolAtom";

/* eslint-enable sort-exports/sort-exports */
